import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getMyEnrollment } from '../api/enrollments';
import { getMyContent, downloadMaterial, type EditionContent, type CourseMaterial, type Speaker, type EditionMaterials } from '../api/content';
import { setPortalToken, getPortalToken } from '../api/client';
import StepIndicator from '../components/StepIndicator';
import StatusBadge from '../components/StatusBadge';
import BadgeModal from '../components/BadgeModal';
import ZoomPlayer from '../components/ZoomPlayer';
import type { EnrollmentStatus } from '../types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const PENDING_STATUSES: EnrollmentStatus[] = [
  'portal_registered', 'lms_enrolled', 'zoom_registration_pending', 'zoom_registered',
];
const SHOW_JOIN_STATUSES: EnrollmentStatus[] = ['zoom_approved', 'join_link_sent', 'attended'];

type Tab = 'relatori' | 'materiali' | 'registrazione' | 'meeting';

function fileIcon(type: string) {
  switch (type) {
    case 'pdf': return '📄';
    case 'ppt': case 'pptx': return '📊';
    case 'video': return '🎬';
    case 'recording': return '🎥';
    case 'link': return '🔗';
    default: return '📎';
  }
}

function formatBytes(bytes?: number | null) {
  if (!bytes) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

// ─── Tab: Relatori ────────────────────────────────────────────────────────────
function TabRelatori({ speakers }: { speakers: Speaker[] }) {
  if (speakers.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '4rem 2rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>👥</div>
        <p style={{ margin: 0, fontFamily: 'var(--font-public)' }}>I relatori verranno comunicati a breve.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.5rem' }}>
      {speakers.map(s => (
        <div key={s.id} style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--gray-100)' }}>
          <div style={{ height: 220, overflow: 'hidden', background: 'var(--gray-100)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {s.photo_url ? (
              <img src={s.photo_url} alt={`${s.nome} ${s.cognome}`}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            ) : (
              <span style={{ fontSize: '4rem', color: 'var(--gray-300)' }}>👤</span>
            )}
          </div>
          <div style={{ padding: '1rem 1.125rem' }}>
            <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--pub-navy)', fontFamily: 'var(--font-public)' }}>
              {s.nome} {s.cognome}
            </div>
            <div style={{ fontSize: '0.8125rem', color: 'var(--pub-orange)', fontWeight: 600, marginTop: '0.2rem' }}>{s.role}</div>
            {s.azienda && <div style={{ fontSize: '0.8125rem', color: 'var(--gray-500)', marginTop: '0.1rem' }}>{s.azienda}</div>}
            {s.bio && <p style={{ margin: '0.625rem 0 0', fontSize: '0.875rem', color: 'var(--gray-600)', lineHeight: 1.55 }}>{s.bio}</p>}
            {s.linkedin_url && (
              <a href={s.linkedin_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', marginTop: '0.75rem', fontSize: '0.8125rem', color: '#0077b5', textDecoration: 'none', fontWeight: 500 }}>
                🔗 LinkedIn
              </a>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Tab: Materiali ───────────────────────────────────────────────────────────
function TabMateriali({ materialsByEdition }: { materialsByEdition: EditionMaterials[] }) {
  const totalDocs = materialsByEdition.reduce((n, e) => n + e.materials.filter(m => m.type !== 'recording' && m.type !== 'video').length, 0);

  if (totalDocs === 0 && materialsByEdition.every(e => e.materials.length === 0)) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '4rem 2rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📁</div>
        <p style={{ margin: 0, fontFamily: 'var(--font-public)' }}>I materiali verranno pubblicati a breve.</p>
      </div>
    );
  }

  const MaterialRow = ({ m }: { m: CourseMaterial }) => (
    <div style={{
      display: 'flex', gap: '1rem', alignItems: 'center',
      background: '#fff', borderRadius: 10, padding: '1rem 1.25rem',
      border: '1px solid var(--gray-100)', opacity: m.accessible ? 1 : 0.6,
    }}>
      <div style={{ fontSize: '1.5rem', width: 40, textAlign: 'center', flexShrink: 0 }}>
        {m.accessible ? fileIcon(m.type) : '🔒'}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '0.9375rem' }}>{m.title}</div>
        {m.description && <div style={{ fontSize: '0.8125rem', color: 'var(--gray-400)', marginTop: '0.15rem' }}>{m.description}</div>}
        {m.file_size_bytes ? <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: '0.1rem' }}>{formatBytes(m.file_size_bytes)}</div> : null}
      </div>
      {m.accessible && (
        m.external_url ? (
          <a href={m.external_url} target="_blank" rel="noopener noreferrer"
            className="btn-pub-cta" style={{ fontSize: '0.8125rem', padding: '0.4rem 1rem', textDecoration: 'none', flexShrink: 0 }}>
            {m.type === 'link' ? 'Apri' : 'Guarda'}
          </a>
        ) : (
          <button className="btn-pub-cta"
            style={{ fontSize: '0.8125rem', padding: '0.4rem 1rem', flexShrink: 0, border: 'none', cursor: 'pointer' }}
            onClick={() => downloadMaterial(m.id, m.title)}>
            Scarica
          </button>
        )
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
      {materialsByEdition.map(edition => {
        const docs = edition.materials.filter(m => m.type !== 'recording' && m.type !== 'video');
        if (docs.length === 0) return null;
        const formattedDate = format(new Date(edition.editionScheduledAt), "EEEE d MMMM yyyy", { locale: it });
        return (
          <div key={edition.editionId}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.875rem' }}>
              <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--pub-navy)', fontFamily: 'var(--font-public)' }}>
                {edition.editionLabel}
              </span>
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', textTransform: 'capitalize' }}>{formattedDate}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
              {docs.map(m => <MaterialRow key={m.id} m={m} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Tab: Registrazione ───────────────────────────────────────────────────────
function TabRegistrazione({ materialsByEdition }: { materialsByEdition: EditionMaterials[] }) {
  const editionsWithRecordings = materialsByEdition
    .map(e => ({ ...e, recordings: e.materials.filter(m => m.type === 'recording' || m.type === 'video') }))
    .filter(e => e.recordings.length > 0);

  if (editionsWithRecordings.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--gray-400)', padding: '4rem 2rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🎥</div>
        <p style={{ margin: 0, fontFamily: 'var(--font-public)' }}>Le registrazioni saranno disponibili dopo le sessioni.</p>
      </div>
    );
  }

  const RecordingCard = ({ r }: { r: CourseMaterial }) => (
    <div style={{ background: '#fff', borderRadius: 12, padding: '1.5rem', border: '1px solid var(--gray-100)', opacity: r.accessible ? 1 : 0.6 }}>
      <h3 style={{ margin: '0 0 0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1rem', color: 'var(--pub-navy)', fontFamily: 'var(--font-public)' }}>
        🎥 {r.title}
      </h3>
      {r.description && <p style={{ color: 'var(--gray-500)', marginBottom: '1rem', fontSize: '0.9375rem' }}>{r.description}</p>}
      {r.accessible ? (
        r.external_url?.includes('youtube') ? (
          <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, borderRadius: 8, overflow: 'hidden' }}>
            <iframe src={r.external_url.replace('watch?v=', 'embed/')}
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
              allowFullScreen title={r.title} />
          </div>
        ) : r.external_url ? (
          <a href={r.external_url} target="_blank" rel="noopener noreferrer"
            className="btn-pub-cta" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
            🎬 Guarda la registrazione
          </a>
        ) : (
          <button className="btn-pub-cta"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', border: 'none', cursor: 'pointer' }}
            onClick={() => downloadMaterial(r.id, r.title)}>
            ⬇ Scarica
          </button>
        )
      ) : (
        <p style={{ margin: 0, color: 'var(--warning)', fontWeight: 500, fontSize: '0.875rem' }}>
          🔒 Non ancora disponibile.
        </p>
      )}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '2.5rem' }}>
      {editionsWithRecordings.map(edition => {
        const formattedDate = format(new Date(edition.editionScheduledAt), "EEEE d MMMM yyyy", { locale: it });
        return (
          <div key={edition.editionId}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.875rem' }}>
              <span style={{ fontWeight: 700, fontSize: '0.9375rem', color: 'var(--pub-navy)', fontFamily: 'var(--font-public)' }}>
                {edition.editionLabel}
              </span>
              <span style={{ fontSize: '0.75rem', color: 'var(--gray-400)', textTransform: 'capitalize' }}>{formattedDate}</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              {edition.recordings.map(r => <RecordingCard key={r.id} r={r} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function PortalAreaPage() {
  const [searchParams] = useSearchParams();
  const [enrollment, setEnrollment] = useState<any | null>(null);
  const [content, setContent] = useState<EditionContent | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>('relatori');
  const [showBadge, setShowBadge] = useState(false);

  const urlToken = searchParams.get('token');
  const token = urlToken ?? getPortalToken() ?? '';

  useEffect(() => { if (urlToken) setPortalToken(urlToken); }, [urlToken]);

  const fetchAll = useCallback(async () => {
    if (!token) { setError('Token mancante. Controlla il link nella tua email.'); setIsLoading(false); return; }
    try {
      const [enrollData, contentData] = await Promise.all([
        getMyEnrollment(token),
        getMyContent(token),
      ]);
      setEnrollment(enrollData);
      setContent(contentData);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.status === 401
        ? 'Link non valido o scaduto. Controlla la tua email.'
        : 'Errore nel caricamento. Riprova tra qualche minuto.');
    } finally { setIsLoading(false); }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!enrollment || !PENDING_STATUSES.includes(enrollment.status)) return;
    const id = setInterval(fetchAll, 30_000);
    return () => clearInterval(id);
  }, [enrollment?.status, fetchAll]);

  if (isLoading) return (
    <div style={{ minHeight: '100vh', background: '#F0F4F8', display: 'flex', flexDirection: 'column' }}>
      <div className="loading-container" style={{ flex: 1 }}><div className="spinner spinner-lg" /></div>
    </div>
  );

  if (error || !enrollment) return (
    <div style={{ minHeight: '100vh', background: '#F0F4F8', display: 'flex', flexDirection: 'column' }}>
      <main className="public-main" style={{ flex: 1 }}>
        <div className="alert alert-danger">{error ?? 'Iscrizione non trovata.'}</div>
        <a href="/register" className="btn-pub-cta" style={{ textDecoration: 'none', marginTop: '1rem', display: 'inline-block' }}>Torna alla registrazione</a>
      </main>
    </div>
  );

  const course = enrollment.edition?.course;
  const edition = enrollment.edition;
  const status = enrollment.status as EnrollmentStatus;
  const speakers = content?.speakers ?? [];
  const materialsByEdition = content?.materialsByEdition ?? [];
  const allMaterials = materialsByEdition.flatMap(e => e.materials);
  const showJoin = SHOW_JOIN_STATUSES.includes(status) && enrollment.zoomJoinUrl;

  let formattedDate = '';
  if (edition?.scheduledAt) {
    formattedDate = format(new Date(edition.scheduledAt), "EEEE d MMMM yyyy 'alle' HH:mm", { locale: it });
  }

  const showZoomPlayer = !course?.inPresenza && edition?.zoomMeetingId && SHOW_JOIN_STATUSES.includes(status);

  const docsCount = allMaterials.filter(m => m.type !== 'recording' && m.type !== 'video').length;
  const recordingsCount = allMaterials.filter(m => m.type === 'recording' || m.type === 'video').length;

  const tabs: { id: Tab; label: string; icon: string; count?: number }[] = [
    { id: 'relatori', label: 'Relatori', icon: '👥', count: speakers.length || undefined },
    { id: 'materiali', label: 'Materiali', icon: '📁', count: docsCount || undefined },
    { id: 'registrazione', label: 'Registrazione', icon: '🎥', count: recordingsCount || undefined },
    ...(showZoomPlayer ? [{ id: 'meeting' as Tab, label: 'Meeting', icon: '📹' }] : []),
  ];

  const statusMessages: Partial<Record<EnrollmentStatus, string>> = {
    portal_registered: 'Iscrizione ricevuta. Stiamo elaborando la richiesta.',
    lms_enrolled: 'Confermata. Stiamo completando la registrazione su Zoom.',
    zoom_registration_pending: 'Registrazione Zoom in corso.',
    zoom_registered: 'Registrazione Zoom completata. In attesa di approvazione.',
    zoom_approved: 'Approvato! Riceverai il link di accesso via email.',
    join_link_sent: 'Il link è pronto. Clicca il pulsante qui sopra.',
    attended: 'Grazie per aver partecipato!',
    no_show: 'Non risulti presente. Contattaci se pensi ci sia un errore.',
    cancelled: 'Iscrizione annullata.',
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Body: two columns */}
      <div style={{ display: 'flex', flex: 1 }}>

        {/* ── Left sidebar ── */}
        <div style={{
          width: 620, flexShrink: 0, background: '#fff',
          borderRight: '1px solid var(--gray-100)',
          display: 'flex', flexDirection: 'column',
          padding: '2.5rem 2rem',
          overflowY: 'auto',
        }}>
          {/* Course name */}
          <div style={{ marginBottom: '2rem' }}>
            <p style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--pub-orange)', marginBottom: '0.5rem', fontFamily: 'var(--font-public)' }}>
              Il tuo corso
            </p>
            <h2 style={{ color: 'var(--pub-navy)', fontFamily: 'var(--font-public)', fontWeight: 800, fontSize: '1.375rem', margin: '0 0 0.25rem', lineHeight: 1.2 }}>
              {course?.name}
            </h2>
            <p style={{ color: 'var(--gray-400)', margin: 0, fontSize: '0.875rem' }}>{edition?.label}</p>
          </div>

          {/* Join button */}
          {showJoin && (
            <a href={enrollment.zoomJoinUrl} target="_blank" rel="noopener noreferrer"
              className="btn-pub-cta"
              style={{ textDecoration: 'none', textAlign: 'center', display: 'block', marginBottom: '1rem', fontSize: '1rem' }}>
              Entra nel Meeting Zoom
            </a>
          )}

          {/* Badge button – solo per corsi in presenza */}
          {course?.inPresenza && (
            <button
              onClick={() => setShowBadge(true)}
              className="btn-pub-outline"
              style={{ width: '100%', marginBottom: '2rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', fontSize: '0.9375rem' }}
            >
              🪪 Genera Badge
            </button>
          )}

          {/* Status */}
          <div style={{ marginBottom: '2rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
              <p style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--gray-400)', margin: 0 }}>
                Stato iscrizione
              </p>
              <StatusBadge status={status} />
            </div>
            <StepIndicator currentStatus={status} inPresenza={!!course?.inPresenza} />
            {statusMessages[status] && (
              <p style={{
                fontSize: '0.8125rem', lineHeight: 1.55,
                color: ['attended', 'zoom_approved', 'join_link_sent'].includes(status) ? 'var(--success)' :
                       ['no_show', 'cancelled'].includes(status) ? 'var(--danger)' : 'var(--gray-500)',
                margin: '0.875rem 0 0', fontFamily: 'var(--font-public)',
              }}>
                {statusMessages[status]}
              </p>
            )}
          </div>

          {/* Date & info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', borderTop: '1px solid var(--gray-100)', paddingTop: '1.5rem' }}>
            {formattedDate && (
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: '0.1rem' }}>📅</span>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.875rem', textTransform: 'capitalize', color: 'var(--gray-700)' }}>{formattedDate}</div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--gray-400)' }}>Durata: {edition?.durationMinutes} min</div>
                </div>
              </div>
            )}
            {enrollment.user && (
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>👤</span>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>{enrollment.user.nome} {enrollment.user.cognome}</span>
              </div>
            )}
            {enrollment.user?.azienda && (
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>🏢</span>
                <span style={{ fontSize: '0.875rem', color: 'var(--gray-700)' }}>{enrollment.user.azienda}</span>
              </div>
            )}
          </div>

          <div style={{ marginTop: 'auto', paddingTop: '2rem', borderTop: '1px solid var(--gray-100)' }}>
            <p style={{ fontSize: '0.8125rem', color: 'var(--gray-400)', margin: 0 }}>
              Problemi? <a href="mailto:marketing@switchonmedia.it" style={{ color: 'var(--pub-blue)' }}>marketing@switchonmedia.it</a>
            </p>
          </div>
        </div>

        {/* ── Right panel ── */}
        <div style={{ flex: 1, background: '#F0F4F8', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
          {/* Tab bar */}
          <div style={{ background: '#fff', borderBottom: '1px solid var(--gray-100)', padding: '0 2rem', display: 'flex', gap: '0', flexShrink: 0 }}>
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
                padding: '1rem 1.25rem',
                border: 'none', borderBottom: activeTab === tab.id ? '2px solid var(--pub-navy)' : '2px solid transparent',
                background: 'transparent', cursor: 'pointer',
                fontWeight: activeTab === tab.id ? 700 : 400,
                color: activeTab === tab.id ? 'var(--pub-navy)' : 'var(--gray-500)',
                fontFamily: 'var(--font-public)', fontSize: '0.9375rem',
                display: 'flex', alignItems: 'center', gap: '0.4rem',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
              }}>
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                {tab.count ? (
                  <span style={{ background: activeTab === tab.id ? 'var(--pub-navy)' : 'var(--gray-200)', color: activeTab === tab.id ? '#fff' : 'var(--gray-500)', borderRadius: 20, fontSize: '0.75rem', padding: '0.1rem 0.5rem', fontWeight: 600 }}>
                    {tab.count}
                  </span>
                ) : null}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ flex: 1, padding: '2rem 2.5rem' }}>
            {activeTab === 'relatori' && <TabRelatori speakers={speakers} />}
            {activeTab === 'materiali' && <TabMateriali materialsByEdition={materialsByEdition} />}
            {activeTab === 'registrazione' && <TabRegistrazione materialsByEdition={materialsByEdition} />}
            {activeTab === 'meeting' && showZoomPlayer && enrollment.user && (
              <ZoomPlayer
                meetingNumber={String(edition.zoomMeetingId)}
                userName={`${enrollment.user.nome} ${enrollment.user.cognome}`}
                userEmail={enrollment.user.email}
                joinUrl={enrollment.zoomJoinUrl}
              />
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        background: 'var(--pub-navy)', color: 'rgba(255,255,255,0.55)',
        fontSize: '0.8125rem', textAlign: 'center', padding: '1rem 2rem',
        fontFamily: 'var(--font-public)', flexShrink: 0,
      }}>
        {import.meta.env.VITE_FOOTER_TEXT}
      </footer>

      {/* Badge modal */}
      {showBadge && enrollment.user && (
        <BadgeModal
          data={{
            nome: enrollment.user.nome,
            cognome: enrollment.user.cognome,
            azienda: enrollment.user.azienda,
            courseName: course?.name ?? '',
            editionLabel: edition?.label,
            token,
            badgeBgUrl: course?.badgeBgUrl,
          }}
          onClose={() => setShowBadge(false)}
        />
      )}
    </div>
  );
}
